import { Badge } from "@devhop/ui";
import { formatDocumentMoney } from "@/shared/lib/performance-document-currency";
import type { EventDetailReportDocumentTimelineItemDTO } from "../../domain/contracts";
import { formatPerformanceDateTime } from "./PerformanceReportsNav";

const DOCUMENT_TYPE_LABELS = {
  quotation: "ໃບສະເໜີລາຄາ",
  invoice: "ໃບເກັບເງິນ",
  receipt: "ໃບຮັບເງິນ",
} as const;

const DOCUMENT_STATUS_LABELS = {
  draft: "ຮ່າງ",
  pending_approval: "ລໍຖ້າອະນຸມັດ",
  approved: "ອະນຸມັດແລ້ວ",
  issued: "ອອກແລ້ວ",
  partial: "ຊຳລະບາງສ່ວນ",
  paid: "ຊຳລະແລ້ວ",
  voided: "ຍົກເລີກ",
} as const;

const STATUS_VARIANTS: Record<
  string,
  "default" | "secondary" | "outline" | "destructive"
> = {
  draft: "secondary",
  pending_approval: "secondary",
  approved: "default",
  issued: "outline",
  partial: "secondary",
  paid: "default",
  voided: "destructive",
};

type EventDocumentTimelineSectionProps = {
  documents: EventDetailReportDocumentTimelineItemDTO[];
  embedded?: boolean;
};

function TimelineRow({
  document,
}: {
  document: EventDetailReportDocumentTimelineItemDTO;
}) {
  const issuedLabel = document.issuedAt
    ? formatPerformanceDateTime(document.issuedAt)
    : formatPerformanceDateTime(document.createdAt);

  return (
    <li className="relative flex gap-4 pb-6 last:pb-0">
      <div className="flex flex-col items-center">
        <span className="mt-1 size-2.5 shrink-0 rounded-full border-2 border-primary bg-background" />
        <span className="mt-1 w-px flex-1 bg-border" aria-hidden />
      </div>
      <div className="min-w-0 flex-1 rounded-lg border bg-muted/10 px-4 py-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-medium text-sm">{document.documentNo}</p>
            <p className="mt-0.5 text-muted-foreground text-xs">
              {DOCUMENT_TYPE_LABELS[document.documentType]} · {issuedLabel}
            </p>
          </div>
          <Badge
            variant={STATUS_VARIANTS[document.status] ?? "secondary"}
            className="shrink-0"
          >
            {DOCUMENT_STATUS_LABELS[
              document.status as keyof typeof DOCUMENT_STATUS_LABELS
            ] ?? document.status}
          </Badge>
        </div>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
          <span className="font-semibold tabular-nums">
            {formatDocumentMoney(document.grandTotal, document.currency)}
          </span>
          {document.documentType === "invoice" &&
          document.remainingAmount > 0 ? (
            <span className="text-muted-foreground text-xs">
              ຄ້າງ{" "}
              {formatDocumentMoney(
                document.remainingAmount,
                document.currency,
              )}
            </span>
          ) : null}
        </div>
      </div>
    </li>
  );
}

export function EventDocumentTimelineSection({
  documents,
  embedded = false,
}: EventDocumentTimelineSectionProps) {
  if (documents.length === 0) {
    return (
      <p className="text-center text-muted-foreground text-sm">
        ຍັງບໍ່ມີເອກະສານ
      </p>
    );
  }

  if (embedded) {
    return (
      <ol>
        {documents.map((document) => (
          <TimelineRow key={document.id} document={document} />
        ))}
      </ol>
    );
  }

  return (
    <section className="overflow-hidden rounded-xl border bg-card">
      <div className="border-b px-5 py-4">
        <p className="font-semibold text-muted-foreground text-xs uppercase tracking-widest">
          ເສັ້ນທາງເອກະສານ
        </p>
        <p className="mt-1 text-muted-foreground text-xs">
          QT · INV · RCPT — read-only
        </p>
      </div>

      <ol className="px-5 py-4">
        {documents.map((document) => (
          <TimelineRow key={document.id} document={document} />
        ))}
      </ol>
    </section>
  );
}
